import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import Icon from 'components/Icon';
import OverlayFooter from 'components/OverlayFooter';
import { noop } from 'helpers/GlobalHelper';
import { getPurifyConfig, IFRAME_TEST_PATTERN } from 'components/Article/ArticleHelpers';
import Header from '../../../Header';
import Style from './PasswordCollection.module.scss';

const PasswordCollection = ({
  email,
  onFieldChange,
  previous,
  headerProps,
  screenProps: {
    buttonText,
    disclosure,
    headline,
    forgotPasswordText,
    onSubmit,
    onResetPassword,
    onLoad = noop,
  },
  isProcessing,
  setEmail,
  language,
  setExistingEmail,
  setScreen,
  signinShowPassword,
}) => {
  let passwordInput = React.createRef();
  const passwordType = signinShowPassword ? 'text' : 'password';
  const [signinGenericError, setGenericError] = useState('');
  const [signinPasswordError, setPasswordError] = useState('');
  let sanitizedDisclosure = disclosure;
  if (!IFRAME_TEST_PATTERN.test(disclosure)) {
    sanitizedDisclosure = DOMPurify.sanitize(disclosure, getPurifyConfig());
  }
  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isProcessing) return false;
      onSubmit({ password: passwordInput.value, setGenericError, setPasswordError });
      return false;
    }
    return false;
  };

  let passwordErrorText;
  let errorText;

  if (signinGenericError) {
    errorText = signinGenericError;
  }

  if (signinGenericError.toLowerCase() === 'invalid loginid') {
    errorText = 'Incorrect password';
  }

  // local error checking
  if (signinPasswordError) {
    passwordErrorText = signinPasswordError;
  }

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <main className={Style.signIn} onKeyPress={handleKeyPress}>
      <Header
        {...headerProps}
        onClose={() => {
          setEmail('');
          setScreen({ current: previous });
        }}
      />
      <div className={Style.signInWrap}>
        <h1>{headline}</h1>
        <div className={Style.signInPasswordContainer}>
          <input
            className={classNames(Style.inputField, Style.emailInput)}
            autoFocus
            disabled
            placeholder="Email"
            type="email"
            name="email"
            value={email}
            autoCapitalize="none"
          />
          <label
            className={Style.passwordLabel}
            onClick={() => {
              setExistingEmail(undefined);
              setScreen({ current: previous });
            }}
          >
            <Icon icon="edit-icon" size="large" />
          </label>
        </div>
        <div className={Style.signInPasswordContainer}>
          <input
            className={Style.inputField}
            autoFocus
            type={passwordType}
            name="signinPassword"
            onChange={e => {
              setGenericError('');
              setPasswordError('');
            }}
            ref={input => {
              passwordInput = input;
            }}
            placeholder="Password"
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <label className={Style.passwordLabel}>
            <input
              className={Style.inputField}
              name="signinShowPassword"
              type="checkbox"
              value={signinShowPassword}
              onChange={e => {
                onFieldChange(e);
                passwordInput.focus();
              }}
            />
            <span />
          </label>
          <div className={Style.error}>{passwordErrorText}</div>
          <div className={Style.error}>{errorText}</div>
        </div>
        <div
          className={Style.disclosure}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitizedDisclosure }}
        />
        <button
          className={Style.btnPrimary}
          onClick={e => {
            e.preventDefault();
            if (isProcessing) return false;
            onSubmit({ password: passwordInput.value, setGenericError, setPasswordError });
            return false;
          }}
        >
          {buttonText}
        </button>
        {forgotPasswordText && (
          <div
            className={Style.forgotPasswordLink}
            onClick={() => {
              onResetPassword();
            }}
          >
            {forgotPasswordText}
          </div>
        )}
      </div>
      <OverlayFooter language={language} />
    </main>
  );
};

export default PasswordCollection;

PasswordCollection.propTypes = {
  email: PropTypes.string,
  handleActivateCode: PropTypes.func,
  headerProps: PropTypes.shape({}),
  isProcessing: PropTypes.bool,
  landingPage: PropTypes.string,
  language: PropTypes.shape({}).isRequired,
  login: PropTypes.func,
  onFieldChange: PropTypes.func,
  previous: PropTypes.string,
  screenProps: PropTypes.shape({}),
  setEmail: PropTypes.func,
  setExistingEmail: PropTypes.func,
  setScreen: PropTypes.func,
  signinShowPassword: PropTypes.bool,
};
